import { MonthYearValue } from '../MonthYearValue';

import { MonthYear, isDate1AfterDate2 } from '../monthYear';

interface IQuantityDestribution {
  name: string;
  distributionMethod: (_quantity: number, _startDate: MonthYear, _dueDate?: MonthYear) => MonthYearValue[];
}

export const futureOrderProductDistributionTypes: IQuantityDestribution[] = [
  {
    name: 'Monthly',
    distributionMethod: (quantity: number, startDate: MonthYear, dueDate?: MonthYear) =>
      DistributeValueThroughDates(quantity, startDate, dueDate, false),
  },
  {
    name: 'Quarterly',
    distributionMethod: (quantity: number, startDate: MonthYear, dueDate?: MonthYear) =>
      DistributeValueThroughDates(quantity, startDate, dueDate, true),
  },
  {
    name: 'Yearly',
    distributionMethod: (quantity: number, startDate: MonthYear, dueDate?: MonthYear) =>
      DistributeValueByYear(quantity, startDate, dueDate),
  },
];

const GenerateMonthYears = (startDate: MonthYear, dueDate: MonthYear): MonthYear[] => {
  const months = Array.from({
    length: (dueDate.year - startDate.year) * 12 + (dueDate.month - startDate.month) + 1,
  }).map((_, i) => ({
    year: startDate.year + Math.floor((i + startDate.month - 1) / 12),
    month: ((i + startDate.month - 1) % 12) + 1,
  }));

  return months;
};

const DistributeValueByMonth = (value: number, months: MonthYear[]): MonthYearValue[] => {
  const distributeArray: MonthYearValue[] = [];
  const monthlyValue = months.length === 0 ? 0 : Math.floor(value / months.length);
  const totalDistributed = monthlyValue * months.length;
  const remainingBalance = value - totalDistributed;
  const addToLastObject = monthlyValue + remainingBalance;

  months.map((currentDate, index) => {
    index === months.length - 1
      ? distributeArray.push({ date: currentDate, value: addToLastObject })
      : distributeArray.push({ date: currentDate, value: monthlyValue });
  });
  return distributeArray;
};

const DistributeValueByX = (value: number, months: MonthYear[], intervals = 3): MonthYearValue[] => {
  const distributeArray: MonthYearValue[] = months.map((month) => ({
    date: {
      month: month.month,
      year: month.year,
    },
    value: 0,
  }));

  if (distributeArray.length === 1) {
    distributeArray[0].value = value;
    return distributeArray;
  }

  if (distributeArray.length <= intervals) {
    distributeArray[0].value = value / 2;
    distributeArray[distributeArray.length - 1].value = value / 2;

    return distributeArray;
  }

  const quarterlyIndexInterval = intervals; // Distributing every third month
  const numberOfPayments = Math.floor((months.length - 1) / quarterlyIndexInterval) + 1;
  const quarterlyValue = Math.floor(value / numberOfPayments);

  // Distribute the quarterly value
  for (let i = 0; i < months.length; i += quarterlyIndexInterval) {
    distributeArray[i].value = quarterlyValue;
  }

  // Calculate remaining balance and add to the last distributed value
  const totalDistributed = quarterlyValue * numberOfPayments;
  const remainingBalance = value - totalDistributed;
  distributeArray[numberOfPayments * quarterlyIndexInterval - 3].value += remainingBalance;

  if (distributeArray[distributeArray.length - 1] != distributeArray[numberOfPayments * quarterlyIndexInterval - 3]) {
    distributeArray[distributeArray.length - 1].value =
      distributeArray[numberOfPayments * quarterlyIndexInterval - 3].value;
    distributeArray[numberOfPayments * quarterlyIndexInterval - 3].value = 0;
  }

  return distributeArray;
};

export const DistributeValueThroughDates = (
  value: number,
  startDate: MonthYear,
  dueDate?: MonthYear,
  spreadByQuarters = true,
): MonthYearValue[] => {
  if (!dueDate) return [{ date: startDate, value: value }];

  if (isDate1AfterDate2(startDate, dueDate)) {
    console.error('StartDate is not valid ');
    return [{ date: startDate, value: value }];
  }
  const monthList = GenerateMonthYears(startDate, dueDate);

  return spreadByQuarters ? DistributeValueByX(value, monthList, 3) : DistributeValueByMonth(value, monthList);
};

const DistributeValueByYear = (quantity: number, startDate: MonthYear, dueDate?: MonthYear): MonthYearValue[] => {
  if (!dueDate) return [{ date: { month: startDate.month, year: startDate.year }, value: quantity }];

  const numOfYears = dueDate.year - startDate.year + 1;
  const valuePerYear = Math.floor(quantity / numOfYears);
  const lastYearValue = valuePerYear + (quantity - valuePerYear * numOfYears);
  const monthList = GenerateMonthYears(startDate, dueDate);

  return monthList.map((item) => ({
    date: item,
    value:
      item.year === dueDate.year
        ? item.month === dueDate.month
          ? lastYearValue
          : 0
        : item.month === startDate.month
        ? valuePerYear
        : 0,
  }));
};
